// OpsMenu — the dark menu on every Ops page (pen Q67), pure.
//
// Brand ("Ops" + the product name), the environment marker (staging or
// production, so the two are never mixed up; none when unknown), the four
// pages, and at the foot the way back and who is signed in. Ops is the
// operator's area (formerly "Administration"). The menu is always dark: a
// `data-theme="dark"` subtree remaps the same tokens.

export type { OpsAccess, OpsFrame };
export { OpsLoadFailed, OpsMenu, OpsNoAccess, OpsState };

import { appName } from "@bilda/app/identity";
import type { OpsEnvironment } from "@bilda/app/ops";
import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { AI_USAGE_PATH } from "../../app/aiUsage/route.js";
import {
    FEEDBACK_PATH,
    OPS_ACCOUNTS_PATH,
    OPS_PATH,
    type OpsPage,
} from "../../app/ops/route.js";
import { Button } from "../Button/Button.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import "../AiUsage/AiUsage.css";
import "./Ops.css";

// Whether the viewer may see Ops (the server decides; a 401/403 on a read
// turns this to "denied").
type OpsAccess = "allowed" | "denied";

// What every Ops page shows around its content, read once by its host.
type OpsFrame = {
    // The signed-in operator's email.
    readonly accountLabel: string | undefined;
    readonly environment: OpsEnvironment;
    // "0.14.2 (a1b2c3d)".
    readonly version: string;
};

const Marker = ({ environment }: { readonly environment: OpsEnvironment }) => {
    const { t } = useLingui();
    switch (environment) {
        case "staging":
            return (
                <span className="ops-menu__env ops-menu__env--staging">
                    <Icon name="dot" size="xs" />
                    {t`Staging`}
                </span>
            );
        case "production":
            return (
                <span className="ops-menu__env ops-menu__env--production">
                    <Icon name="dot" size="xs" />
                    {t`Production`}
                </span>
            );
        case "unknown":
            return null;
        default:
            return exhaustSwitchCase(environment);
    }
};

const Item = ({
    href,
    icon,
    current,
    children,
}: {
    readonly href: string;
    readonly icon: IconName;
    readonly current: boolean;
    readonly children: ReactNode;
}) => (
    <a
        className="ai-usage__nav-item"
        href={href}
        aria-current={current ? "page" : undefined}
    >
        <Icon name={icon} size="sm" />
        {children}
    </a>
);

const OpsMenu = ({
    frame,
    access,
    current,
}: {
    readonly frame: OpsFrame;
    readonly access: OpsAccess;
    readonly current: OpsPage;
}) => {
    const { t } = useLingui();
    return (
        <nav
            className="ai-usage__sidebar ops-menu"
            data-theme="dark"
            aria-label={t`Ops`}
        >
            <div className="ops-menu__top">
                <div className="ai-usage__brand">
                    <span className="ai-usage__brand-mark" aria-hidden="true">
                        <Icon name="ops" size="sm" />
                    </span>
                    <span className="ai-usage__brand-text">
                        <span className="ai-usage__brand-title">{t`Ops`}</span>
                        <span className="ai-usage__brand-sub">{appName}</span>
                    </span>
                </div>
                <Marker environment={frame.environment} />
            </div>
            {access === "denied" ? null : (
                <div className="ai-usage__nav">
                    <Item
                        href={OPS_PATH}
                        icon="ops-overview"
                        current={current === "overview"}
                    >
                        {t`Overview`}
                    </Item>
                    <Item
                        href={FEEDBACK_PATH}
                        icon="ops-feedback"
                        current={current === "feedback"}
                    >
                        {t`Feedback`}
                    </Item>
                    <Item
                        href={AI_USAGE_PATH}
                        icon="sparkle"
                        current={current === "ai-usage"}
                    >
                        {t`AI usage and cost`}
                    </Item>
                    <Item
                        href={OPS_ACCOUNTS_PATH}
                        icon="ops-accounts"
                        current={current === "accounts"}
                    >
                        {t`Accounts`}
                    </Item>
                </div>
            )}
            <div className="ai-usage__sidebar-foot">
                <a className="ai-usage__back" href="/">
                    <Icon name="chevron-left" size="sm" />
                    {t`Back to ${appName}`}
                </a>
                {frame.accountLabel === undefined ||
                access === "denied" ? null : (
                    <span className="ai-usage__account ops-menu__account">
                        <span className="ai-usage__account-name">
                            {frame.accountLabel}
                        </span>
                        <span className="ai-usage__account-role">
                            {t`Operator · version ${frame.version}`}
                        </span>
                    </span>
                )}
            </div>
        </nav>
    );
};

// A whole-page or whole-part state: an icon, a title, a line, an action.
const OpsState = ({
    icon,
    title,
    body,
    children,
}: {
    readonly icon: IconName;
    readonly title: string;
    readonly body: string;
    readonly children?: ReactNode;
}) => (
    <div className="ai-usage__state">
        <span className="ai-usage__state-icon" aria-hidden="true">
            <Icon name={icon} size="md" />
        </span>
        <h2 className="ai-usage__state-title">{title}</h2>
        <p className="ai-usage__state-body">{body}</p>
        {children}
    </div>
);

// Only admin accounts reach Ops; anyone else gets this, never the data.
const OpsNoAccess = () => {
    const { t } = useLingui();
    return (
        <OpsState
            icon="shield"
            title={t`You don't have access to Ops`}
            body={t`Only operators see Ops. Ask an operator if you need it.`}
        >
            <a className="ai-usage__state-link" href="/">
                {t`Back to ${appName}`}
            </a>
        </OpsState>
    );
};

// One part's read failed: that part says so, the rest of the page stays.
const OpsLoadFailed = ({
    title,
    onRetry,
}: {
    readonly title: string;
    readonly onRetry: () => void;
}) => {
    const { t } = useLingui();
    return (
        <OpsState
            icon="warning"
            title={title}
            body={t`The server did not answer as expected. Nothing was changed.`}
        >
            <Button variant="secondary" onClick={onRetry}>
                {t`Try again`}
            </Button>
        </OpsState>
    );
};
